import { useSelector } from 'react-redux';
import Media from 'react-media';
import loadable from '@loadable/component';

import { useTheme, Text } from '@simplywallst/ui-core';
import type { Theme } from '@simplywallst/ui-core';

import { getUi } from '@/redux/ui/selectors';

import { useSnackbar } from '@/features/snackbar/context';

import SwitcherContainer from '@/containers/SwitcherContainer';

import Onboarding from '@/components/Onboarding';
import { BrazeProvider } from '@/components/Braze';
import { LayoutProvider } from '@/components/Layout/context/LayoutContext';

import { useUser } from '@/hooks/useUser';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { useAppInit } from './hooks/useAppInit';

import { Body } from './styled';
import { MainContainer } from './components/MainContainer';
import { AuthChecker } from '@/components/AuthChecker';
import { SimplyWallStSchema } from '@/components/DocumentSchema';

import { Suspense } from 'react';
import { useFlag } from '@/features/unleash';
import {
  ROUTE_STOCKS,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
} from '@/constants/routes';
import { useLocation } from '@/hooks/useLocation';

const Snackbar = loadable(() => import('@/features/snackbar'), {
  resolveComponent: (c) => c.Snackbar,
  ssr: false,
});

const LoadingOverlay = loadable(() => import('@/components/LoadingOverlay'), {
  ssr: false,
});

const MobileFooterMenu = loadable(
  () => import('@/components/Nav/MobileFooterMenu')
);

const TrialEndedPlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/OurPlans/components/TrialEndedPlansModal/TrialEndedPlansModal'
    )
);

const SubscriptionRecovery = loadable(
  () => import('@/components/SubscriptionRecovery'),
  { resolveComponent: (c) => c.SubscriptionRecovery }
);

const CheckoutReturnModal = loadable(
  () => import('@/components/OurPlans/components/CheckoutReturnModal')
);

const AppUpdate = loadable(() => import('@/components/AppUpdate'), {
  ssr: false,
  resolveComponent: (component) => component.AppUpdate,
});

const AppUrlListener = loadable(() => import('@/components/AppUrlListener'), {
  resolveComponent: (component) => component.AppUrlListener,
});

export interface WithThemeProps {
  theme: Theme;
}

export interface Props {
  layout: Record<string, unknown>;
}

const Content = ({ layout: rawLayout }: Props) => {
  const theme = useTheme();
  const ui = useSelector(getUi);
  const snackbar = useSnackbar();
  const { data: user } = useUser();
  const { isMobile, isTablet } = useUserAgent();

  const isLoading = ui.loading;
  const hasSnackbar = snackbar.state.keys.length > 0;

  // Temporary way of defining the stock routes as having a sub
  // nav without changing the routes object
  const hasSubNavUpdates =
    useFlag({ name: 'mono-sub-nav-updates' }).variant.name === 'variant';
  const hasMobileTopbarSubNav =
    [
      ROUTE_STOCKS_HOME,
      ROUTE_STOCKS_MARKET,
      ROUTE_STOCKS_MARKET_INDUSTRY,
      ROUTE_STOCKS,
    ].includes(useLocation().type) && hasSubNavUpdates;
  const layout = hasMobileTopbarSubNav
    ? { mobileTopbarSubNav: true }
    : rawLayout;

  useAppInit();

  return (
    <MainContainer hasMobileTopbarSubNav={hasMobileTopbarSubNav}>
      <BrazeProvider>
        <LayoutProvider layout={layout}>
          <Body
            justifyContent="center"
            hasMobileFooterMenu={layout && layout.mobileFooterMenu !== false}
          >
            <AuthChecker />
            <SimplyWallStSchema />
            <Suspense
              fallback={
                <Text color="white" typography="small">
                  Loading...
                </Text>
              }
            >
              <SwitcherContainer />
            </Suspense>
          </Body>
          {layout && layout.mobileFooterMenu !== false && (
            <Media
              query={{ maxWidth: theme.mdMaxRaw }}
              defaultMatches={isMobile || isTablet}
              render={() => <MobileFooterMenu />}
            />
          )}
          {layout && (
            <>
              {hasSnackbar && <Snackbar />}
              {isLoading && <LoadingOverlay />}
              <TrialEndedPlansModal />
              <SubscriptionRecovery />
              {user.loggedIn && <CheckoutReturnModal />}
              <Onboarding />
            </>
          )}
          {RUNTIME_ENV === 'native' && user.loggedIn && <AppUpdate />}
          {RUNTIME_ENV === 'native' && <AppUrlListener />}
        </LayoutProvider>
      </BrazeProvider>
    </MainContainer>
  );
};

export default Content;
